import React, { useState, useEffect, useRef, useCallback } from 'react';
import './App.css';
import portfolioData from './data/portfolio.json';
import Sidebar from './components/Sidebar';
import ChatArea from './components/ChatArea';
import IdentityPanel from './components/IdentityPanel';
import EvidenceWeave from './components/EvidenceWeave';
import { findBestMatch } from './utils/contentMatcher';

const greetingDecorator =
  String.raw`(?:(?!_)\p{P}|\p{Zs}|\p{S}(?:\uFE0F)?(?:\u200D\p{S}(?:\uFE0F)?)*)`;
const greetingOnlyPattern = new RegExp(
  `^(hello|hi|hey|good morning|good afternoon|good evening)(?:${greetingDecorator}+there)?${greetingDecorator}*$`,
  'u'
);

function App() {
  const [messages, setMessages] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [currentConversationId, setCurrentConversationId] = useState(null);
  const [inputText, setInputText] = useState('');
  const [isDarkMode, setIsDarkMode] = useState(() => {
    return localStorage.getItem('portfolioTheme') === 'dark';
  });
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeThread, setActiveThread] = useState(() => {
    const hash = window.location.hash.replace('#', '');
    return ['fluxtrade', 'research', 'interfaces'].includes(hash)
      ? hash
      : 'fluxtrade';
  });
  const conversationTopicsRef = useRef(new Set());
  const initializedRef = useRef(false);
  const nextIdRef = useRef(0);
  const inputTextRef = useRef('');
  const consumedFollowupsRef = useRef(new Set());
  const pendingConversationRef = useRef(null);
  const menuButtonRef = useRef(null);

  const sections = portfolioData.sections;
  const contentLibrary = portfolioData.contentLibrary;

  const closeSidebar = useCallback(() => {
    setSidebarOpen(false);
    requestAnimationFrame(() => {
      menuButtonRef.current?.focus({ preventScroll: true });
    });
  }, []);

  const createMessage = (
    sender,
    text,
    options = [],
    followups = [],
    contentId = null
  ) => {
    return {
      id: ++nextIdRef.current,
      sender,
      text,
      options,
      followups,
      contentId,
      timestamp: new Date().toLocaleTimeString()
    };
  };

  const addMessage = (sender, text, options = [], followups = []) => {
    const message = createMessage(sender, text, options, followups);
    setMessages(prev => [...prev, message]);
  };

  useEffect(() => {
    if (!initializedRef.current) {
      addMessage('bot',
        "Hi, I'm **Chun-Yi**. I work across data, machine learning, and backend " +
        "systems. FluxTrade is the project I'm most excited to talk about because " +
        "it brings Python, Rust, data pipelines, and careful testing together. " +
        "You can also ask about my work at NEC, Deutsche Börse, or AlphaLoan.",
        [],
        sections.general.followups
      );
      initializedRef.current = true;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Save theme preference to localStorage
  useEffect(() => {
    localStorage.setItem('portfolioTheme', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);

  useEffect(() => {
    const pendingConversation = pendingConversationRef.current;
    if (!currentConversationId) return;

    setConversations(prev => {
      const existingIndex = prev.findIndex(
        conversation => conversation.id === currentConversationId
      );
      if (existingIndex >= 0) {
        if (prev[existingIndex].messages === messages) return prev;

        const updatedConversations = [...prev];
        updatedConversations[existingIndex] = {
          ...updatedConversations[existingIndex],
          messages
        };
        return updatedConversations;
      }
      if (!pendingConversation || pendingConversation.id !== currentConversationId) {
        return prev;
      }
      return [...prev, {
        ...pendingConversation,
        messages
      }];
    });
    if (pendingConversation?.id === currentConversationId) {
      pendingConversationRef.current = null;
    }
  }, [currentConversationId, messages]);

  const startNewConversation = (sectionKey) => {
    const section = sections[sectionKey];
    const conversationId = ++nextIdRef.current;
    const conversationTitle = `${section.title} - ${new Date().toLocaleDateString()}`;
    const botMessage = createMessage(
      'bot',
      section.content,
      [],
      section.followups,
      sectionKey
    );

    pendingConversationRef.current = {
      id: conversationId,
      title: conversationTitle,
      section: sectionKey,
      topics: []
    };
    setCurrentConversationId(conversationId);
    setMessages([botMessage]);
    closeSidebar();
  };

  const loadConversation = (conversationId) => {
    const conversation = conversations.find(c => c.id === conversationId);
    if (conversation) {
      setCurrentConversationId(conversationId);
      setMessages(conversation.messages.map(message => (
        consumedFollowupsRef.current.has(message.id)
          ? { ...message, followupsConsumed: true }
          : message
      )));
      closeSidebar();
    }
  };

  const appendExchange = (input, sourceMessageId = null) => {
    const response = generateResponse(input);
    const userMessage = createMessage('user', input);
    const botMessage = createMessage(
      'bot',
      response.text,
      [],
      response.followups,
      response.contentId
    );
    setMessages(prev => [
      ...prev.map(message => (
        message.id === sourceMessageId
          ? { ...message, followupsConsumed: true }
          : message
      )),
      userMessage,
      botMessage
    ]);
  };

  const handleOptionClick = (sourceMessageId, option) => {
    if (consumedFollowupsRef.current.has(sourceMessageId)) return;

    consumedFollowupsRef.current.add(sourceMessageId);
    appendExchange(option, sourceMessageId);
  };

  const handleInputSubmit = (e) => {
    e.preventDefault();
    const userInput = inputTextRef.current.trim();
    if (!userInput) return;

    inputTextRef.current = '';
    setInputText('');
    appendExchange(userInput);
  };

  const handleInputChange = (e) => {
    inputTextRef.current = e.target.value;
    setInputText(e.target.value);
  };

  const detectTopicAndSaveConversation = (input) => {
    const lowerInput = input.normalize('NFC').toLowerCase();
    let detectedTopic = 'general';
    let conversationTitle = 'General Conversation';

    if (greetingOnlyPattern.test(lowerInput)) {
      detectedTopic = 'general';
      conversationTitle = 'Getting Started';
    } else if (lowerInput.match(/\b(experience|work|job|career|employment|position|role)\b/)) {
      detectedTopic = 'experience';
      conversationTitle = 'Work Experience Discussion';
      conversationTopicsRef.current.add('experience');
    } else if (lowerInput.match(/\b(project|portfolio|build|built|created|developed|app|website|software)\b/)) {
      detectedTopic = 'projects';
      conversationTitle = 'Technical Projects';
      conversationTopicsRef.current.add('projects');
    } else if (lowerInput.match(/\b(skill|technology|tech|programming|language|framework|tool|stack)\b/)) {
      detectedTopic = 'skills';
      conversationTitle = 'Technical Skills';
      conversationTopicsRef.current.add('skills');
    } else if (lowerInput.match(/\b(award|achievement|recognition|accomplishment|honor|certificate)\b/)) {
      detectedTopic = 'awards';
      conversationTitle = 'Achievements & Awards';
      conversationTopicsRef.current.add('awards');
    } else if (lowerInput.match(/\b(contact|email|reach|connect|hire|collaboration|opportunity)\b/)) {
      detectedTopic = 'contact';
      conversationTitle = 'Contact & Opportunities';
    } else if (lowerInput.match(/\b(education|degree|university|college|study|learn|course)\b/)) {
      detectedTopic = 'education';
      conversationTitle = 'Education & Learning';
    } else if (lowerInput.match(/\b(about|tell me|who|what|describe|background|bio|summary)\b/)) {
      detectedTopic = 'general';
      conversationTitle = 'About & Background';
    }

    // Auto-create conversation if we don't have one yet and we have actual user interaction
    if (!currentConversationId && !pendingConversationRef.current) {
      const conversationId = ++nextIdRef.current;
      setCurrentConversationId(conversationId);
      pendingConversationRef.current = {
        id: conversationId,
        title: conversationTitle,
        section: detectedTopic,
        topics: Array.from(conversationTopicsRef.current)
      };
    }

    return detectedTopic;
  };

  const generateResponse = (input) => {
    const lowerInput = input.normalize('NFC').toLowerCase();

    // Detect topic and auto-save conversation
    detectTopicAndSaveConversation(input);

    // Smart content matching for detailed responses (projects, experiences)
    const matchedContent = findBestMatch(input, contentLibrary);
    if (matchedContent) {
      return {
        text: matchedContent.content,
        followups: matchedContent.followups,
        contentId: matchedContent.id
      };
    }

    // Greeting patterns
    if (greetingOnlyPattern.test(lowerInput)) {
      return {
        text: "Hi, I'm Chun-Yi. Ask me about FluxTrade, my work experience, or my NEC research.",
        followups: sections.general.followups
      };
    }

    // Project patterns - must contain project/portfolio keywords (supports plural)
    // IMPORTANT: Check BEFORE experience pattern to handle "What projects have you worked on?"
    if (lowerInput.match(/\b(projects?|portfolio|build|built|created|developed)\b/)) {
      return { text: sections.projects.content, followups: sections.projects.followups };
    }

    // Skills patterns - must contain skill/technology keywords (supports plural)
    if (lowerInput.match(/\b(skills?|technolog(y|ies)|tech|programming|languages?|frameworks?|tools?|stack)\b/)) {
      return { text: sections.skills.content, followups: sections.skills.followups };
    }

    // Experience patterns - must contain experience/work/job keywords
    if (lowerInput.match(/\b(experience|works?|worked|jobs?|career|employment|position|role)\b/)) {
      return { text: sections.experience.content, followups: sections.experience.followups };
    }

    // Awards patterns
    if (lowerInput.match(/\b(awards?|achievements?|recognition|accomplishments?|honors?|certificates?)\b/)) {
      return { text: sections.awards.content, followups: sections.awards.followups };
    }

    // Contact patterns
    if (lowerInput.match(/\b(contact|email|reach|connect|hire|collaboration|opportunity)\b/)) {
      return { text: sections.contact.content, followups: sections.contact.followups };
    }

    // Education patterns
    if (lowerInput.match(/\b(education|degree|university|college|study|academic)\b/)) {
      return { text: sections.education.content, followups: sections.education.followups };
    }

    // CV patterns
    if (lowerInput.match(/\b(cv|resume|full overview|complete profile|overview|履歷)\b/)) {
      return { text: sections.cv.content, followups: sections.cv.followups };
    }

    // About/general patterns - only for general questions without specific keywords
    if (lowerInput.match(/\b(about|who|background|bio|summary)\b/) && !lowerInput.match(/\b(project|experience|work|skill|education|award)\b/)) {
      return {
        text: sections.general.content,
        followups: sections.general.followups
      };
    }

    // Catch-all fallback for remaining questions
    return {
      text: "I haven't covered that here yet. Try asking about FluxTrade, my work experience, or my education.",
      followups: sections.general.followups
    };
  };

  const handleExplore = (question, threadId) => {
    setActiveThread(threadId);
    window.history.replaceState(null, '', `#${threadId}`);
    appendExchange(question);
  };

  return (
    <div
      className={`app ${isDarkMode ? 'dark-mode' : 'light-mode'}`}
      data-testid="portfolio-app"
    >
      <main
        id="top"
        className="portfolio-shell"
        aria-hidden={sidebarOpen || undefined}
      >
        <IdentityPanel
          personal={portfolioData.personal}
          onExplore={handleExplore}
        />
        <div className="knowledge-panel">
          <EvidenceWeave
            activeThread={activeThread}
            onExplore={handleExplore}
          />
          <ChatArea
            messages={messages}
            contentLibrary={contentLibrary}
            inputText={inputText}
            onInputChange={handleInputChange}
            onInputSubmit={handleInputSubmit}
            onOptionClick={handleOptionClick}
          />
        </div>
        <nav className="contact-links" aria-label="Contact and profiles">
          <span>{portfolioData.personal.location}</span>
          <a href={`mailto:${portfolioData.personal.email}`}>Email</a>
          <a
            href="https://github.com/AustinChen123"
            target="_blank"
            rel="noreferrer"
          >
            GitHub <span className="visually-hidden">(opens in a new tab)</span>
          </a>
        </nav>
      </main>

      <header className="site-header" aria-hidden={sidebarOpen || undefined}>
        <a className="site-header__name" href="#top">CYC / portfolio</a>
        <div className="site-header__actions">
          <button
            type="button"
            onClick={() => setIsDarkMode(!isDarkMode)}
            aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {isDarkMode ? 'Light' : 'Dark'}
          </button>
          <button
            ref={menuButtonRef}
            type="button"
            onClick={() => setSidebarOpen(true)}
            aria-expanded={sidebarOpen}
            aria-controls="portfolio-menu"
          >
            Index
          </button>
        </div>
      </header>

      {sidebarOpen && (
        <button
          type="button"
          className="drawer-backdrop"
          aria-hidden="true"
          tabIndex="-1"
          onClick={closeSidebar}
        />
      )}
      <Sidebar
        portfolioData={portfolioData}
        conversations={conversations}
        currentConversationId={currentConversationId}
        sidebarOpen={sidebarOpen}
        onLoadConversation={loadConversation}
        onStartNewConversation={startNewConversation}
        onCloseSidebar={closeSidebar}
      />
    </div>
  );
}

export default App;
